import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Search,
  Inbox,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  CheckCircle2,
  Clock,
  LifeBuoy,
  X,
  Mail,
  Phone,
  Building,
  User,
  Save,
  ExternalLink,
} from "lucide-react";
import PageHeader from "../../../components/shared/PageHeader";
import {
  getSupportTickets,
  getSupportTicket,
  updateSupportTicket,
} from "../../../api/supportTickets";

// "Resolved" was merged into "Closed" - a closed ticket is understood to be
// resolved. RESOLVED stays here only so any legacy row still renders a pill.
const STATUS_STYLES = {
  OPEN: { cls: "bg-blue-100 text-blue-700", label: "Open", icon: Clock },
  IN_PROGRESS: { cls: "bg-amber-100 text-amber-700", label: "In Progress", icon: RefreshCw },
  RESOLVED: { cls: "bg-emerald-100 text-emerald-700", label: "Closed", icon: CheckCircle2 },
  CLOSED: { cls: "bg-emerald-100 text-emerald-700", label: "Closed", icon: CheckCircle2 },
};

const STATUS_TABS = [
  { id: "ALL", label: "All" },
  { id: "OPEN", label: "Open" },
  { id: "IN_PROGRESS", label: "In Progress" },
  { id: "CLOSED", label: "Closed" },
];

const INQUIRY_TYPE_OPTIONS = [
  { value: "", label: "All topics" },
  { value: "GENERAL_INQUIRY", label: "General Inquiry" },
  { value: "PROPERTY_INQUIRY", label: "Property Inquiry" },
  { value: "AUCTION_INFORMATION", label: "Auction Information" },
  { value: "PRIVATE_SALE_INFORMATION", label: "Private Sale Information" },
  { value: "HOUSE_LAND_INQUIRY", label: "House & Land Inquiry" },
  { value: "SELLING_MY_PROPERTY", label: "Selling My Property" },
  { value: "TECHNICAL_ISSUE", label: "Technical Issue" },
  { value: "FEEDBACK", label: "Feedback" },
  { value: "OTHER", label: "Other" },
];

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

// Legacy rows may still carry RESOLVED - seed the Status dropdown as Closed
// so the select never lands on a value it no longer offers.
const normalizeStatus = (status) =>
  status === "RESOLVED" ? "CLOSED" : status || "OPEN";

const StatusPill = ({ status }) => {
  const meta = STATUS_STYLES[status] || STATUS_STYLES.OPEN;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${meta.cls}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const SupportTicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    closed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [inquiryType, setInquiryType] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailDraft, setDetailDraft] = useState({
    status: "OPEN",
    admin_notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Debounce search to avoid hammering the API on every keystroke
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(id);
  }, [search]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (inquiryType) params.inquiry_type = inquiryType;
      if (debouncedSearch) params.search = debouncedSearch;

      const { data } = await getSupportTickets(params);
      setTickets(data.results || []);
      setTotalCount(data.count || 0);
      if (data.counts) setCounts(data.counts);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load support tickets."
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, inquiryType, debouncedSearch, page, pageSize]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Whenever filters change, jump back to page 1
  useEffect(() => {
    setPage(1);
  }, [statusFilter, inquiryType, debouncedSearch]);

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setDetailDraft({
      status: normalizeStatus(ticket.status),
      admin_notes: ticket.admin_notes || "",
    });
    setDetailLoading(true);
    try {
      const { data } = await getSupportTicket(ticket.id);
      setSelectedTicket(data);
      setDetailDraft({
        status: normalizeStatus(data.status),
        admin_notes: data.admin_notes || "",
      });
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load ticket detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDrawer = () => {
    setSelectedTicket(null);
    setDetailDraft({ status: "OPEN", admin_notes: "" });
  };

  const handleSave = async () => {
    if (!selectedTicket) return;
    setSaving(true);
    try {
      const { data } = await updateSupportTicket(selectedTicket.id, {
        status: detailDraft.status,
        admin_notes: detailDraft.admin_notes,
      });
      setSelectedTicket(data);
      // Patch the row in the list so the badge updates without a full reload
      setTickets((prev) =>
        prev.map((t) => (t.id === data.id ? { ...t, ...data } : t))
      );
      toast.success("Ticket updated");
      // Refresh counts in the background
      loadTickets();
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.detail || "Failed to update ticket. Try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize]
  );

  return (
    <div className="w-full min-h-screen bg-gray-50/80">
      <PageHeader
        title="Support Tickets"
        subtitle="Issues, questions, and feedback submitted by buyers from the Buyer Portal."
        action={
          <button
            type="button"
            onClick={loadTickets}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:border-[#55C5D0]/40 hover:text-[#55C5D0] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        }
      />

      {/* Status tabs + topic filter + search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex flex-col gap-4 p-5 border-b border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_TABS.map((tab) => {
              const isActive = statusFilter === tab.id;
              const count =
                tab.id === "ALL"
                  ? counts.total
                  : tab.id === "OPEN"
                  ? counts.open
                  : tab.id === "IN_PROGRESS"
                  ? counts.in_progress
                  : counts.closed;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${
                    isActive
                      ? "bg-[#55C5D0] text-white border-[#55C5D0]"
                      : "bg-gray-50 text-gray-600 border-gray-100 hover:border-gray-200"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`text-[11px] px-1.5 rounded-md ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-white text-gray-500 border border-gray-200"
                    }`}
                  >
                    {count ?? 0}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative w-full sm:w-80 group">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400 group-focus-within:text-[#55C5D0] transition-colors" />
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ref, name, email, subject…"
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 focus:border-[#55C5D0] focus:bg-white transition-all placeholder:text-gray-400"
              />
            </div>
            <select
              value={inquiryType}
              onChange={(e) => setInquiryType(e.target.value)}
              className="w-full sm:w-60 py-2.5 px-3 text-sm bg-gray-50 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 focus:border-[#55C5D0] focus:bg-white transition-all"
            >
              {INQUIRY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-[#55C5D0]/30 border-t-[#55C5D0] animate-spin mb-4" />
            <p className="text-sm font-medium text-gray-500">Loading support tickets…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20">
            <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Inbox className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-gray-700 mb-1">No support tickets found</p>
            <p className="text-sm text-gray-500 max-w-sm">
              When buyers submit issues from the Buyer Portal Support button, they'll
              show up here.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Buyer</th>
                    <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</th>
                    <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Topic</th>
                    <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-4 px-5 text-xs font-bold text-gray-500 uppercase tracking-wider">Submitted</th>
                    <th className="w-10 py-4 px-2" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tickets.map((t) => (
                    <tr
                      key={t.id}
                      onClick={() => openTicket(t)}
                      className="group bg-white hover:bg-[#55C5D0]/5 transition-colors cursor-pointer"
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#55C5D0]/10 text-[#55C5D0] flex items-center justify-center">
                            <LifeBuoy className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
                            <p className="text-xs text-gray-500 truncate">{t.email}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">Ref: {t.reference_number}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <p className="text-sm font-semibold text-gray-800 truncate max-w-[260px]">{t.subject}</p>
                        {t.property_address && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 truncate max-w-[260px]">
                            <Building className="w-3 h-3 flex-shrink-0" />
                            {t.property_address}
                          </p>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <span className="inline-block text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-md">
                          {t.inquiry_type_display}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <StatusPill status={t.status} />
                      </td>
                      <td className="py-4 px-5 text-sm text-gray-500">{formatDateTime(t.created_at)}</td>
                      <td className="py-4 px-2">
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#55C5D0] transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden p-4 space-y-3">
              {tickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => openTicket(t)}
                  className="w-full text-left rounded-2xl p-4 bg-white border border-gray-100 shadow-sm hover:border-[#55C5D0]/30 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{t.name}</p>
                      <p className="text-xs text-gray-500 truncate">{t.email}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Ref: {t.reference_number}</p>
                    </div>
                    <StatusPill status={t.status} />
                  </div>
                  <p className="text-sm font-medium text-gray-800 mt-3 truncate">{t.subject}</p>
                  <p className="text-xs text-gray-500 mt-2">{formatDateTime(t.created_at)}</p>
                </button>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50/40">
              <p className="text-xs text-gray-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-gray-700">
                  {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)}
                </span>{" "}
                of <span className="font-semibold text-gray-700">{totalCount}</span> tickets
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#55C5D0]/40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>
                <span className="text-sm font-semibold text-gray-700 px-2">
                  Page {page} / {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#55C5D0]/40 transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail drawer */}
      {selectedTicket && (
        <div className="fixed inset-0 z-[80] flex">
          <div
            className="flex-1 bg-black/40 backdrop-blur-sm"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <div className="w-full sm:max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            {/* Drawer header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    {selectedTicket.reference_number}
                  </span>
                  <StatusPill status={selectedTicket.status} />
                </div>
                <h2 className="text-lg font-bold text-gray-900 truncate">{selectedTicket.subject}</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  {selectedTicket.inquiry_type_display} · {formatDateTime(selectedTicket.created_at)}
                </p>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 flex-shrink-0"
                aria-label="Close ticket detail"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 px-6 py-5 space-y-5">
              {detailLoading && (
                <div className="text-xs text-gray-500">Refreshing latest details…</div>
              )}

              {/* Contact card */}
              <section className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3">Contact</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-semibold">{selectedTicket.name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a
                      href={`mailto:${selectedTicket.email}`}
                      className="text-[#55C5D0] hover:underline truncate"
                    >
                      {selectedTicket.email}
                    </a>
                  </div>
                  {selectedTicket.phone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a
                        href={`tel:${selectedTicket.phone}`}
                        className="text-[#55C5D0] hover:underline"
                      >
                        {selectedTicket.phone}
                      </a>
                    </div>
                  )}
                  {selectedTicket.property_address && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Building className="w-4 h-4 text-gray-400" />
                      <span className="truncate">{selectedTicket.property_address}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Message */}
              <section>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Message</p>
                <div className="bg-white border border-gray-100 rounded-xl p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {selectedTicket.message}
                </div>
              </section>

              {/* Status + admin notes */}
              <section className="space-y-3">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Triage</p>

                <div>
                  <label className="text-xs font-bold text-gray-700">Status</label>
                  <select
                    value={detailDraft.status}
                    onChange={(e) =>
                      setDetailDraft((prev) => ({ ...prev, status: e.target.value }))
                    }
                    className="mt-1.5 w-full py-2 px-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 focus:border-[#55C5D0]"
                  >
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-700">Internal notes</label>
                  <textarea
                    value={detailDraft.admin_notes}
                    onChange={(e) =>
                      setDetailDraft((prev) => ({
                        ...prev,
                        admin_notes: e.target.value,
                      }))
                    }
                    rows={4}
                    placeholder="Track conversations, follow-ups, or resolution details — not visible to the buyer."
                    className="mt-1.5 w-full py-2 px-3 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/25 focus:border-[#55C5D0] resize-none"
                  />
                </div>

                {selectedTicket.resolved_at && (
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Closed {formatDateTime(selectedTicket.resolved_at)}
                  </p>
                )}
              </section>
            </div>

            {/* Drawer footer actions */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sticky bottom-0">
              <a
                href={`mailto:${selectedTicket.email}?subject=Re: ${encodeURIComponent(
                  selectedTicket.subject
                )} (${selectedTicket.reference_number})`}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-[#55C5D0] hover:text-[#3bb3be] transition-colors"
              >
                <Mail className="w-4 h-4" />
                Reply by email
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#55C5D0] hover:bg-[#3bb3be] text-white text-sm font-bold shadow-md shadow-[#55C5D0]/20 disabled:opacity-60 transition-all"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" /> Save changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsPage;
